import { ApolloQueryResult } from "@apollo/client";
import { UserDetailsQuery, UserFragment } from "@dashboard/graphql";
import { GetExternalAccessTokenData, GetExternalAuthUrlData, LoginData } from "@saleor/sdk";


export interface RequestExternalLoginInput {
  redirectUri: string;
  /** PKCE (RFC 7636) S256 challenge; the Web Shop Manager sign-in requires it (wsmClerk.ts). */
  codeChallenge?: string;
}

export interface ExternalLoginInput {
  code: string;
  state: string;
  /** The PKCE verifier the challenge was made from, kept in this tab since the sign-in started. */
  codeVerifier?: string;
}

export interface RequestExternalLogoutInput {
  returnTo: string;
}

export const UserContextError = {
  loginError: "loginError",
  serverError: "serverError",
  noPermissionsError: "noPermissionsError",
  externalLoginError: "externalLoginError",
  loginAttemptDelay: "loginAttemptDelay",
  unknownLoginError: "unknownLoginError",
  invalidCredentials: "invalidCredentials",
} as const;

export type UserContextError = (typeof UserContextError)[keyof typeof UserContextError];

export interface UserContext {
  login?: (username: string, password: string) => Promise<LoginData | undefined>;
  loginByExternalPlugin?: (
    pluginId: string | null,
    input: ExternalLoginInput,
  ) => Promise<GetExternalAccessTokenData | undefined>;
  /** The session ended by the app: expiry, a revoked token. The route signs in again. */
  logout?: () => Promise<void>;
  /** The person chose to sign out: the route waits on a button (wsmClerk.ts). */
  signOut?: () => Promise<void>;
  requestLoginByExternalPlugin?: (
    pluginId: string,
    input: RequestExternalLoginInput,
  ) => Promise<GetExternalAuthUrlData | undefined>;
  user?: UserFragment | null;
  authenticating: boolean;
  isCredentialsLogin: boolean;
  authenticated: boolean;
  errors: UserContextError[];
  /**
   * What the API said when it refused the last external sign-in, in its own words
   * (no staff account at that address, a deactivated one), for the sign-in route to
   * show instead of sending the browser straight back to the provider.
   */
  loginMessage?: string | null;
  refetchUser?: () => Promise<ApolloQueryResult<UserDetailsQuery>>;
}
